import React from "react";
import { Zap, Table2, Sigma, ListChecks, Trophy } from "lucide-react";
import { Blocks } from "./Blocks";
import QuestionCard, { LETTERS } from "./QuestionCard";

const today = () => new Date().toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" });
const ICON = { card: Zap, table: Table2, formula: Sigma, skills: ListChecks };
const noop = () => {};

function Cover({ code, title, subtitle, lines = [] }) {
  return (
    <section className="print-cover">
      <span className="eyebrow">Vize Laboratuvarı · {code} · {today()}</span>
      <h1 className="page-title">{title}</h1>
      <p className="page-sub">{subtitle}</p>
      {lines.length > 0 && (
        <ol className="nb-ol print-toc">
          {lines.map((l, i) => <li key={i}>{l}</li>)}
        </ol>
      )}
    </section>
  );
}

// Sitedeki görünümün birebir aynısı: gerçek bileşenler, gerçek stiller.
// Sayfa bölme @media print kurallarıyla (break-before / break-inside) yapılır.
export default function PrintDoc({ job }) {
  if (!job) return null;
  const { kind, course } = job;

  if (kind === "notes") {
    return (
      <div className="print-doc" data-course={course.key}>
        <Cover
          code={course.code}
          title={course.name}
          subtitle="Tam Sunum Ders Notları + Sınav Öncesi Hap Bilgiler"
          lines={[...course.notes.map((u) => `${u.no}: ${u.title}`), "Sınav Öncesi Hap Bilgiler"]}
        />
        {course.notes.map((u) => (
          <article className="notes-body print-page" key={u.id}>
            <div className="unit-hero">
              <span className="eyebrow">{course.code} · {u.no}</span>
              <h1 className="unit-title">{u.title}</h1>
              <p className="unit-meta">{u.sections.length} bölüm · Tam sunum ders notu</p>
            </div>
            {u.sections.map((s, i) => (
              <section key={i} className="nb-section">
                <h2 className="nb-h2">{s.h}</h2>
                <Blocks blocks={s.blocks} />
              </section>
            ))}
          </article>
        ))}
        <section className="print-page">
          <div className="section-head">
            <span className="eyebrow">{course.code} · Hap Bilgiler</span>
            <h1 className="page-title">Sınav Öncesi Hap Bilgiler</h1>
            <p className="page-sub">Tam sunum özetleri, karşılaştırma tabloları ve formül kartları.</p>
          </div>
          <div className="summary-grid print-summary">
            {course.summary.map((s, i) => {
              const Icon = ICON[s.kind] || Zap;
              return (
                <div key={i} className={`sum-card sum-${s.kind}`}>
                  <div className="sum-head">
                    <span className="sum-icon"><Icon size={15} /></span>
                    <h3>{s.h}</h3>
                  </div>
                  <Blocks blocks={s.blocks} />
                </div>
              );
            })}
          </div>
        </section>
      </div>
    );
  }

  if (kind === "exam") {
    const { exam, ans } = job;
    let c = 0, w = 0;
    exam.questions.forEach((q, i) => { if (ans[i] !== undefined) (ans[i] === q.a ? c++ : w++); });
    const blank = exam.questions.length - c - w;
    return (
      <div className="print-doc" data-course={course.key}>
        <Cover code={course.code} title={exam.title} subtitle={`${course.name} · Sınav + Çözümler Karnesi`} />
        <div className="result-card">
          <Trophy size={30} />
          <div>
            <h3>{c}/{exam.questions.length} doğru · Puan {c * 4}</h3>
            <p>{w} yanlış · {blank} boş · Her soru 4 puan</p>
          </div>
        </div>
        <div className="nb-table-wrap print-keep">
          <table className="nb-table print-answers">
            <thead><tr><th>Soru</th>{exam.questions.map((_, i) => <th key={i}>{i + 1}</th>)}</tr></thead>
            <tbody>
              <tr><td><b>Cevabın</b></td>{exam.questions.map((q, i) => <td key={i} className={ans[i] === undefined ? "" : ans[i] === q.a ? "cell-ok" : "cell-bad"}>{ans[i] === undefined ? "–" : LETTERS[ans[i]]}</td>)}</tr>
              <tr><td><b>Anahtar</b></td>{exam.questions.map((q, i) => <td key={i}>{LETTERS[q.a]}</td>)}</tr>
            </tbody>
          </table>
        </div>
        <div className="q-list print-qlist">
          {exam.questions.map((q, i) => (
            <QuestionCard key={i} q={q} number={i + 1} chosen={ans[i] === undefined ? q.a : ans[i]} onAnswer={noop} testPrefix="print-q" />
          ))}
        </div>
        <p className="print-note">Boş bıraktığın sorularda doğru cevap ve çözüm gösterilmiştir.</p>
      </div>
    );
  }

  if (kind === "mistakes") {
    return (
      <div className="print-doc" data-course={course.key}>
        <Cover code={course.code} title="Hata Defterim" subtitle={`${job.list.length} yanlış soru · şık analizi ve adım adım çözümleriyle`} />
        <div className="q-list print-qlist">
          {job.list.map((m) => (
            <div key={m.key} className="mb-item">
              <div className="mb-meta">
                <span className="badge">{m.course.code} · {m.exam.title} · Soru {m.qi + 1}</span>
                <span className="muted">İlk cevabın: {LETTERS[m.chosen]}</span>
              </div>
              <QuestionCard q={m.q} number={m.qi + 1} chosen={m.chosen} onAnswer={noop} testPrefix={`print-mb-${m.key.replace(/[^a-z0-9]/gi, "")}`} />
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
}
